import Angular from "angular";
import Loader from "angular-ecmascript/module-loader";
import {Service} from "angular-ecmascript/module-helpers";
import Filter from "../model/filter.model";
import Settings from "../providers/settings.provider";
import Booking from "../model/booking.model";
import Processor from "../model/processor.model";
import BookingColor from "../model/booking-color.model.js";
import BookingComment from "main/model/booking-comment.model.js";

export let Name = 'Bookings';

export class BookingsFilter extends Filter {

    static load = function (name) {
        return Filter.load(name, BookingsFilter);
    };

    constructor(data) {
        super(data);

        this.sortBy = 'startDate';
        this.sortType = Filter.Sort.ASC;
        this.search = null;
        this.dateStart = null;
        this.dateEnd = null;
        this.state = null;
        this.withStorages = false;
        this.branchId = null;
        this.bookingId = null;
        this.serviceId = null;
        this.resourceId = null;

        if (data) {
            Object.assign(this, data);
        }
    }

    active() {
        return this.search != null
            || this.dateStart != null
            || this.dateEnd != null
            || this.state != null
            || this.withStorages === true
            || this.branchId != null
            || this.bookingId != null
            || this.serviceId != null
            || this.resourceId != null;
    };

    reset() {
        super.reset();
        this.sortBy = 'startDate';
        this.sortType = Filter.Sort.ASC;
        this.search = null;
        this.dateStart = null;
        this.dateEnd = null;
        this.state = null;
        this.withStorages = false;
        this.bookingId = null;
        this.branchId = null;
        this.serviceId = null;
        this.resourceId = null;
    }

}

export class Bookings extends Service {
    static $name = Name;
    static $inject = ['$q', 'Restangular', 'CacheFactory', 'settings'];

    create() {
        // NOOP.
    }

    get(bookingId) {
        var defer = this.$q.defer();

        this.Restangular
            .setBaseUrl(this.settings.httpUrl('app'))
            .one('booking', bookingId)
            .get()
            .then((response) => {
                defer.resolve(this.Restangular.copy(new Booking(response)));
            })
            .catch((err) => {
                defer.reject(err);
            });

        return defer.promise;
    }

    find(bookingsFilter) {
        let defer = this.$q.defer();

        this.Restangular
            .setBaseUrl(this.settings.httpUrl('app'))
            .all('bookings')
            .post(bookingsFilter)
            .then((response) => {
                response.data = response.data.map((bookingData) => {
                    let booking = this.Restangular.restangularizeElement('', new Booking(bookingData), 'booking');
                    booking.fromServer = true;
                    return booking;
                });

                defer.resolve(response);
            })
            .catch((err) => {
                defer.reject(err);
            });

        return defer.promise;
    }

    save(booking) {
        let defer = this.$q.defer();
        let plainBooking = this.Restangular.stripRestangular(booking);

        this.Restangular
            .setBaseUrl(this.settings.httpUrl('app'))
            .one('booking', booking.id)
            .customPUT(plainBooking)
            .then(() => {
                booking.version += 1;
                defer.resolve();
            })
            .catch((err) => {
                defer.reject(err);
            });

        return defer.promise;
    }

    allProcessors() {
        let defer = this.$q.defer();

        this.Restangular
            .setBaseUrl(this.settings.httpUrl('app'))
            .one('processors', this.settings.branch.id)
            .withHttpConfig({cache: this.CacheFactory.get('http')})
            .getList()
            .then((response) => {
                let data = response.map((processorData) => {
                    return this.Restangular.copy(new Processor(processorData));
                });

                defer.resolve(data);
            })
            .catch((err) => {
                defer.reject(err);
            });

        return defer.promise;
    }

    colors() {
        let defer = this.$q.defer();

        this.Restangular
            .setBaseUrl(this.settings.httpUrl('app'))
            .all('config/' + this.settings.branch.id + '/booking/colors')
            .withHttpConfig({cache: this.CacheFactory.get('http')})
            .getList()
            .then((response) => {
                var colors = response.map((colorData) => {
                    return this.Restangular.copy(new BookingColor(colorData));
                });

                defer.resolve(colors);
            })
            .catch((err) => {
                defer.reject(err);
            });


        return defer.promise;
    }

    comments() {
        let defer = this.$q.defer();

        this.Restangular
            .setBaseUrl(this.settings.httpUrl('app'))
            .all('config/' + this.settings.branch.id + '/booking/comments')
            .withHttpConfig({cache: this.CacheFactory.get('http')})
            .getList()
            .then((response) => {
                var comments = response.map((commentData) => {
                    return this.Restangular.copy(new BookingComment(commentData));
                });

                defer.resolve(comments);
            })
            .catch((err) => {
                defer.reject(err);
            });


        return defer.promise;
    }

    /**
     * Find all free dates.
     *
     * @param {Object} options
     *
     *  month: Month number.
     *  services: Array of services to book.
     *  resources: Array of resources.
     */
    freeDates(month, year, services, resources, duration) {
        let defer = this.$q.defer();

        let serviceIds = services.map((service) => service.id).join(',');
        let resourceIds = resources.map((resource) => resource.id).join(',');

        this.Restangular
            .setBaseUrl(this.settings.httpUrl())
            .one('booking', 'dates')
            .get({
                branchid: this.settings.branch.id,
                serviceid: serviceIds,
                resourceid: resourceIds,
                duration: duration,
                month: month + 1,
                year: year,
                completeWeeks: false
            })
            .then((data) => {
                defer.resolve(data.dates);
            })
            .catch((response) => {
                defer.reject(response.data);
            });

        return defer.promise;
    }

    /**
     * Find all free times on an specific date.
     *
     * @param {Object} options
     *
     *  date: Date to get the free times for.
     *  services: Array of services to book.
     *  resources: Array of resources.
     */
    freeTimes(date, services, resources, duration, interval) {
        let defer = this.$q.defer();

        let serviceIds = services.map((service) => service.id).join(',');
        let resourceIds = resources.map((resource) => resource.id).join(',');

        this.Restangular
            .setBaseUrl(this.settings.httpUrl())
            .one('booking', 'times')
            .get({
                branchid: this.settings.branch.id,
                serviceid: serviceIds,
                resourceid: resourceIds,
                date: date,
                duration: duration,
                interval: interval
            })
            .then((data) => {
                defer.resolve(data.times);
            })
            .catch((response) => {
                defer.reject(response.data);
            });

        return defer.promise;
    }

    createBooking(booking) {
        let defer = this.$q.defer();

        booking.source = 'source.neonis.private';
        booking.creator = this.settings.user.name;

        this.Restangular
            .setBaseUrl(this.settings.httpUrl('booking'))
            .all('booking')
            .post(booking, {bookingid: '-1'})
            .then((response) => {
                defer.resolve(response.bookingid);
            })
            .catch((response) => {
                defer.reject(response.data);
            });

        return defer.promise
    }

}

let BookingsModule = Angular.module('bookings-service', ['restangular', Settings.name]);

new Loader(BookingsModule)
    .load(Bookings);

export default BookingsModule;



// WEBPACK FOOTER //
// ./src/modules/main/services/bookings.service.js